import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, Clock, Check, MessageSquare, X, Send } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const convId = (a, b) => "conv_" + [a, b].sort().join("_");

export function ConnectButton({ userId, name, suggestedNote = "", prompts = [], size = "md", onChange }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("loading");
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || user.user_id === userId) { setState("self"); return; }
    api.get(`/connections/state/${userId}`).then((r) => setState(r.data.state)).catch(() => setState("none"));
  }, [userId, user]);

  const openModal = () => {
    setNote(suggestedNote || `Hi ${name?.split(" ")[0] || ""} — I'd love to connect on Ikigora.`);
    setOpen(true);
  };

  const sendRequest = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/connections/${userId}/request`, { note });
      setState(r.data.state);
      setOpen(false);
      toast.success(r.data.state === "connected" ? "You're connected!" : "Connection request sent");
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send request");
    } finally { setBusy(false); }
  };

  const accept = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/connections/${userId}/accept`);
      setState("connected");
      toast.success("Connected — say hello!");
      navigate(`/messages/${r.data.conversation_id || convId(user.user_id, userId)}`);
    } catch (e) {
      toast.error("Could not accept");
    } finally { setBusy(false); }
  };

  if (state === "self" || state === "loading") return null;

  const cls = size === "sm"
    ? "px-3 py-1.5 text-xs"
    : "px-4 py-2 text-sm";
  const base = `inline-flex items-center justify-center gap-1.5 rounded-md font-semibold transition-colors ${cls}`;

  return (
    <>
      {state === "none" && (
        <button onClick={openModal} data-testid={`connect-button-${userId}`}
          className={`${base} bg-cobalt text-white hover:bg-gold-hover`}>
          <UserPlus size={15} /> Connect
        </button>
      )}
      {state === "pending_out" && (
        <button disabled data-testid={`connect-pending-${userId}`}
          className={`${base} border border-border text-muted-foreground`}>
          <Clock size={15} /> Requested
        </button>
      )}
      {state === "pending_in" && (
        <button onClick={accept} disabled={busy} data-testid={`connect-accept-${userId}`}
          className={`${base} bg-emerald text-white hover:opacity-90`}>
          <Check size={15} /> Accept
        </button>
      )}
      {state === "connected" && (
        <button onClick={() => navigate(`/messages/${convId(user.user_id, userId)}`)} data-testid={`connect-message-${userId}`}
          className={`${base} border border-border hover:border-cobalt hover:text-cobalt`}>
          <MessageSquare size={15} /> Message
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-navy/70 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)} data-testid="connect-modal">
          <div className="bg-card border border-border rounded-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl font-bold">Connect with {name}</h3>
              <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <p className="text-xs text-muted-foreground mb-2">Add a note — they'll see it when they accept.</p>
            {prompts.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="connect-prompts">
                {prompts.map((p, i) => (
                  <button key={i} onClick={() => setNote(p)} data-testid={`connect-prompt-${i}`}
                    className="text-[11px] text-left border border-border rounded-full px-2.5 py-1 hover:border-cobalt hover:text-cobalt transition-colors">
                    {p.length > 46 ? p.slice(0, 44) + "…" : p}
                  </button>
                ))}
              </div>
            )}
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} maxLength={500}
              data-testid="connect-note-input"
              className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-cobalt transition-colors resize-none" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setOpen(false)} className="border border-border rounded-md px-4 py-2 text-sm hover:bg-secondary/60 transition-colors">Cancel</button>
              <button onClick={sendRequest} disabled={busy} data-testid="connect-send-request"
                className="inline-flex items-center gap-1.5 bg-cobalt text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-gold-hover transition-colors disabled:opacity-50">
                <Send size={14} /> Send request
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
